import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Images } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

/**
 * PhotoGallery — inline photo(s) with a fullscreen lightbox viewer.
 * Supports multiple photos (swipe / arrows) and click-to-zoom.
 *
 * Props:
 *  - photos: string[]   (base64 data-urls or urls; empties are filtered out)
 *  - alt: string
 *  - imgClassName: string  (classes applied to the inline preview <img>)
 *  - fill: boolean         (make the inline preview fill its parent container)
 *  - testid: string
 */
export default function PhotoGallery({ photos = [], alt = "", imgClassName = "", fill = false, testid = "photo-gallery" }) {
  const { t } = useLang();
  const list = (photos || []).filter(Boolean);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  const touchX = useRef(null);
  const lbRef = useRef(null);

  // The lightbox is portalled to document.body — OUTSIDE any Radix Dialog DOM
  // subtree. Radix dismisses the Dialog on a native `pointerdown` that bubbles to
  // document. We stop that native event at the lightbox root (bubble phase) so it
  // never reaches document (child buttons still receive it first), preventing the
  // parent Dialog/fiche from closing when navigating photos.
  useEffect(() => {
    const el = lbRef.current;
    if (!open || !el) return;
    const stop = (e) => e.stopPropagation();
    el.addEventListener("pointerdown", stop);
    el.addEventListener("mousedown", stop);
    return () => {
      el.removeEventListener("pointerdown", stop);
      el.removeEventListener("mousedown", stop);
    };
  }, [open]);

  const go = useCallback((d) => {
    setZoom(false);
    setIdx((i) => (i + d + list.length) % list.length);
  }, [list.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        // The lightbox lives in a body-portal inside a Radix Dialog. Stop the
        // event so Escape only closes the lightbox (returns to the fiche),
        // not the parent Dialog underneath.
        e.stopPropagation();
        e.preventDefault();
        setOpen(false);
      } else if (e.key === "ArrowRight" && list.length > 1) go(1);
      else if (e.key === "ArrowLeft" && list.length > 1) go(-1);
    };
    window.addEventListener("keydown", onKey, true);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey, true); document.body.style.overflow = ""; };
  }, [open, go, list.length]);

  if (list.length === 0) return null;

  const openAt = (i) => { setIdx(i); setZoom(false); setOpen(true); };

  return (
    <>
      <div className={fill ? "relative w-full h-full" : "relative"} data-testid={testid}>
        <button type="button" onClick={() => openAt(0)}
          className={`block w-full ${fill ? "h-full" : ""} cursor-zoom-in`} data-testid={`${testid}-open`}>
          <img src={list[0]} alt={alt}
            className={imgClassName || (fill ? "w-full h-full object-cover" : "w-full h-52 object-cover rounded-xl")} />
        </button>
        {list.length > 1 && (
          <span className={`absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/65 text-white px-2.5 py-1 text-xs font-semibold pointer-events-none`}
            data-testid={`${testid}-count`}>
            <Images size={13} /> 1/{list.length}
          </span>
        )}
      </div>

      {open && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center select-none pointer-events-auto"
          ref={lbRef}
          style={{ pointerEvents: "auto" }}
          data-testid={`${testid}-lightbox`}
          data-photogallery-lightbox="1"
          onClick={() => setOpen(false)}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => { e.stopPropagation(); touchX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (touchX.current == null || list.length < 2) return;
            const dx = e.changedTouches[0].clientX - touchX.current;
            if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
            touchX.current = null;
          }}>
          <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            className="absolute top-4 right-4 h-11 w-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            data-testid={`${testid}-close`} aria-label={t("hc.close")}><X size={22} /></button>

          <button type="button" onClick={(e) => { e.stopPropagation(); setZoom((z) => !z); }}
            className="absolute top-4 left-4 h-11 w-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            data-testid={`${testid}-zoom`} aria-label="Zoom">{zoom ? <ZoomOut size={20} /> : <ZoomIn size={20} />}</button>

          {list.length > 1 && (
            <>
              <button type="button" onClick={(e) => { e.stopPropagation(); go(-1); }}
                className="absolute left-3 md:left-6 h-12 w-12 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                data-testid={`${testid}-prev`} aria-label={t("hc.previous")}><ChevronLeft size={26} /></button>
              <button type="button" onClick={(e) => { e.stopPropagation(); go(1); }}
                className="absolute right-3 md:right-6 h-12 w-12 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                data-testid={`${testid}-next`} aria-label={t("hc.next")}><ChevronRight size={26} /></button>
            </>
          )}

          <div className="relative">
            <img src={list[idx]} alt={alt}
              onClick={(e) => { e.stopPropagation(); setZoom((z) => !z); }}
              className={`max-h-[88vh] max-w-[92vw] object-contain rounded-lg transition-transform duration-300 ${zoom ? "scale-150 cursor-zoom-out" : "cursor-zoom-in"}`}
              data-testid={`${testid}-image`} />
          </div>

          {list.length > 1 && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {list.map((_, i) => (
                <button key={i} type="button" onClick={() => { setIdx(i); setZoom(false); }}
                  className={`h-2.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-2.5 bg-white/40 hover:bg-white/70"}`}
                  data-testid={`${testid}-dot-${i}`} aria-label={`Photo ${i + 1}`} />
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
